import { useState, useEffect, useCallback } from 'react';
import { Upload, FileText, FileCheck, Trash2, RefreshCw, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface KbStatus {
  exists: boolean;
  filename?: string;
  uploadedAt?: string;
  size?: number;
}

const KB_PASSWORD_KEY = 'line_kb_admin_password';

export function LineKBPage() {
  const [password, setPassword] = useState(() => localStorage.getItem(KB_PASSWORD_KEY) || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [status, setStatus] = useState<KbStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const fetchStatus = useCallback(async (pwd: string) => {
    setLoadingStatus(true);
    try {
      const res = await fetch('/api/line/kb/status', {
        headers: { 'X-Admin-Password': pwd },
      });
      if (res.status === 401) {
        setIsAuthenticated(false);
        showMessage('error', 'Incorrect password');
        return;
      }
      const data = await res.json() as KbStatus;
      setStatus(data);
      setIsAuthenticated(true);
      localStorage.setItem(KB_PASSWORD_KEY, pwd);
    } catch {
      showMessage('error', 'Failed to connect to server');
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  // Auto-authenticate if password is already saved
  useEffect(() => {
    if (password) {
      fetchStatus(password);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    fetchStatus(password);
  };

  const uploadFile = async (file: File) => {
    const allowedExtensions = ['.txt', '.md', '.csv', '.pdf'];
    const isAllowed = allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!isAllowed) {
      showMessage('error', 'Unsupported file type. Use .txt, .md, .csv, or .pdf');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/line/kb/upload', {
        method: 'POST',
        headers: { 'X-Admin-Password': password },
        body: formData,
      });

      const data = await res.json() as { success?: boolean; error?: string; filename?: string; characters?: number };

      if (!res.ok || data.error) {
        showMessage('error', data.error || 'Upload failed');
        return;
      }

      showMessage('success', `Uploaded "${data.filename}" (${data.characters?.toLocaleString()} characters)`);
      await fetchStatus(password);
    } catch {
      showMessage('error', 'Upload failed — check your connection');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await uploadFile(file);
  };

  const handleDelete = async () => {
    if (!confirm('Delete the knowledge base? The bot will stop using it immediately.')) return;
    setDeleting(true);
    try {
      const res = await fetch('/api/line/kb/clear', {
        method: 'DELETE',
        headers: { 'X-Admin-Password': password },
      });
      if (res.ok) {
        showMessage('success', 'Knowledge base deleted');
        await fetchStatus(password);
      } else {
        showMessage('error', 'Failed to delete');
      }
    } catch {
      showMessage('error', 'Delete failed — check your connection');
    } finally {
      setDeleting(false);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
  };

  // ── Login screen ──────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md w-full max-w-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="h-6 w-6 text-blue-600" />
            <h1 className="text-xl font-semibold">LINE Bot Admin</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Password
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                autoFocus
              />
            </div>
            {message && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {message.text}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={!password || loadingStatus}>
              {loadingStatus ? 'Checking...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ── Main admin UI ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">LINE Bot Knowledge Base</h1>
            <p className="text-sm text-gray-500 mt-1">
              Upload a file to give the bot information to answer questions from.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchStatus(password)}
            disabled={loadingStatus}
          >
            <RefreshCw className={`h-4 w-4 ${loadingStatus ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Toast message */}
        {message && (
          <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success'
              ? <CheckCircle className="h-4 w-4 flex-shrink-0" />
              : <AlertCircle className="h-4 w-4 flex-shrink-0" />
            }
            {message.text}
          </div>
        )}

        {/* Current KB status */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
            Current Knowledge Base
          </h2>

          {status?.exists ? (
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <FileCheck className="h-8 w-8 text-green-500 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">{status.filename || 'knowledge-base.txt'}</p>
                  <p className="text-sm text-gray-500">
                    {status.size ? formatSize(status.size) : ''}
                    {status.uploadedAt ? ` · Uploaded ${formatDate(status.uploadedAt)}` : ''}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={deleting}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-gray-500">
              <FileText className="h-8 w-8 text-gray-300" />
              <p className="text-sm">No knowledge base uploaded yet. The bot will answer general questions only.</p>
            </div>
          )}
        </div>

        {/* Upload area */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            {status?.exists ? 'Replace Knowledge Base' : 'Upload Knowledge Base'}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Upload a plain text, Markdown, or CSV file. For PDFs, export to text first for best results.
            The bot will use this file to answer questions.
          </p>

          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors ${
              uploading
                ? 'border-blue-300 bg-blue-50'
                : isDragging
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Upload className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
            <p className="text-sm text-gray-600 mb-3">
              {uploading ? 'Uploading...' : 'Drag and drop a file here, or click to select'}
            </p>
            <input
              type="file"
              accept=".txt,.md,.csv,.pdf,text/plain,text/markdown,text/csv,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="kb-upload"
              disabled={uploading}
            />
            <label htmlFor="kb-upload">
              <Button type="button" variant="outline" disabled={uploading} asChild>
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Select File'}
                </span>
              </Button>
            </label>
          </div>
        </div>

        {/* Sign out */}
        <div className="text-center">
          <button
            className="text-sm text-gray-400 hover:text-gray-600"
            onClick={() => {
              localStorage.removeItem(KB_PASSWORD_KEY);
              setIsAuthenticated(false);
              setPassword('');
            }}
          >
            Sign out
          </button>
        </div>

      </div>
    </div>
  );
}
